<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="mx-auto max-w-4xl">
      <Breadcrumb :crumbs="breadcrumbItems" />
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl mb-6 mt-4">
        <h2 class="mb-4 text-xl font-semibold text-gray-900">
          {{ currentContent.title }}
        </h2>
        <video
          v-if="signedVideoUrl"
          :key="signedVideoUrl"
          class="w-full h-auto max-w-full rounded-xl border border-gray-200"
          controls
        >
          <source
            :src="signedVideoUrl"
            type="video/mp4"
          >
          Your browser does not support the video tag.
        </video>
        <div class="flex flex-col gap-3 mt-4 md:flex-row md:items-center md:justify-between">
          <h2 class="text-sm font-medium text-gray-700">
            Next Video: VeEX Module Update Tutorial
          </h2>
          <button
            class="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c9ad99]"
            @click="onContentComplete(currentModule._id, currentContent._id)"
          >
            Mark as Complete
            <CheckCircleIcon class="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
      <Accordion
        :is-open="currentModule.show"
        :progress="computeModuleProgress()"
        @update:is-open="currentModule.show = $event"
      >
        <!-- Title Slot -->
        <template #title>
          {{ currentModule.title }}
        </template>
        <!-- Content Count Slot -->
        <template #content-count>
          {{ getContentCount(currentModule) }}
        </template>
        <!-- Progress Label Slot -->
        <template #progress-label>
          Progress
        </template>
        <!-- Progress Percent Slot -->
        <template #progress-percent>
          {{ computeModuleProgress() }}% Complete
        </template>
        <!-- Default Slot: List of Contents -->
        <template #default>
          <div class="space-y-3">
            <div
              v-for="(content, index) in currentModule.contents"
              :key="index"
              class="content-item flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2"
            >
              <div class="flex items-center">
                <div class="flex items-center mr-3">
                  <input
                    :id="'checkbox-' + index"
                    :checked="content.completed"
                    class="w-4 h-4 rounded-full border border-gray-300 bg-white text-primary appearance-none checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary"
                    type="checkbox"
                    @change="onContentComplete(currentModule._id, content._id)"
                  >
                </div>
                <div
                  class="relative mr-3 inline-flex items-center justify-center w-9 h-9 overflow-hidden rounded-full bg-[#f6f1ec] border border-gray-200"
                >
                  <PlayCircleIcon class="h-5 w-5 text-gray-700" />
                </div>
                <router-link
                  :to="'/dashboard/course/' + course._id + '/' + currentModule._id + '/' + content._id"
                  class="text-gray-900 text-sm font-medium"
                >
                  {{ content.title }}
                </router-link>
              </div>
              <span class="text-xs text-gray-500">{{ content.length }} min</span>
            </div>
          </div>
        </template>
      </Accordion>
    </section>
  </main>
</template>
<script lang="ts">
import {computed, defineComponent, inject, onMounted, ref, watch, watchEffect} from 'vue';
import axios from 'axios';
import {useRouter} from 'vue-router';
import Breadcrumb from '@/components/Breadcrumb.vue';
import {BreadcrumbItem, Content, Course, Module} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import Accordion from "@/components/Accordion.vue";
import {CheckCircleIcon, PlayCircleIcon} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "Module",
  components: {Accordion, Breadcrumb, CheckCircleIcon, PlayCircleIcon},
  props: {
    courseId: {
      type: String,
      required: true,
    },
    moduleId: {
      type: String,
      required: true,
    },
    contentId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    const {settings} = inject(settingsKey)!;

    const course = ref<Course>({
      _id: '',
      title: '',
      description: '',
      modules: [],
      createdAt: '',
    });

    const currentModule = ref<Module>({
      _id: '',
      title: '',
      description: '',
      contents: [],
      show: false,
    });

    const currentContent = ref<Content>({
      _id: '',
      title: '',
      type: 'video',
      description: '',
      videoUrl: '',
      length: 0,
      completed: false,
    });

    const signedVideoUrl = ref<string>('');
    const loading = ref<boolean>(false);
    const error = ref<any>(null);

    const breadcrumbItems = computed<BreadcrumbItem[]>(() => [
      {text: 'Dashboard', href: '/dashboard'},
      {text: course.value.title, href: `/dashboard/course/${course.value._id}`},
      {text: currentModule.value?.title || "Module", href: '', isDivider: true},
    ]);

    const getSignedUrlForVideo = async (fileName: string): Promise<string> => {
      if (!fileName) return '';
      try {
        const response = await axios.get(`/video/get-signed-url?fileName=${encodeURIComponent(fileName)}`);
        return response.data.url;
      } catch (err) {
        console.error('Error fetching signed URL:', err);
        return fileName;
      }
    };

    const fetchCourseDetails = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Course>(`course/detail/${props.courseId}`);
        course.value = response.data || {
          _id: '',
          title: '',
          description: '',
          modules: []
        };

        const foundModule = course.value.modules?.find((mod) => mod._id === props.moduleId);
        if (foundModule) {
          currentModule.value = {
            ...foundModule,
            contents: foundModule.contents || [],
            show: true
          };
        }

        const foundContent = currentModule.value.contents?.find(
            (content) => content?._id === props.contentId
        );

        if (foundContent) {
          currentContent.value = foundContent;
          signedVideoUrl.value = await getSignedUrlForVideo(foundContent.videoUrl || '');
        }

        loading.value = false;
      } catch (err) {
        console.error(err);
        loading.value = false;
      }
    };

    watch(
        () => currentContent.value?.videoUrl,
        async (newUrl) => {
          if (newUrl) {
            signedVideoUrl.value = await getSignedUrlForVideo(newUrl);
          }
        },
        {immediate: true}
    );

    watch(
        [() => props.courseId, () => props.moduleId, () => props.contentId],
        fetchCourseDetails
    );

    onMounted(async () => {
      await fetchCourseDetails();
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: currentModule.value?.title,
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    const toggleModule = (): void => {
      if (currentModule.value) {
        currentModule.value.show = !currentModule.value.show;
      }
    };

    const getContentCount = (module: Module): string => {
      let videoCount = 0;
      let documentCount = 0;
      let quizCount = 0;

      for (const content of module.contents) {
        if (!Object.prototype.hasOwnProperty.call(content, 'type')) {
          console.error(`Content object doesn't have 'type' property: `, content);
          continue;
        }
        switch (content.type) {
          case 'video':
            videoCount++;
            break;
          case 'article':
            documentCount++;
            break;
          case 'quiz':
            quizCount++;
            break;
          default:
            console.error(`Unknown content type: ${content.type}`);
        }
      }

      return `${videoCount} Videos, ${documentCount} Resources, ${quizCount} Quizzes`;
    };

    // Compute module progress percentage based on completed contents
    const computeModuleProgress = (): number => {
      if (!currentModule.value.contents || currentModule.value.contents.length === 0) return 0;
      const completedCount = currentModule.value.contents.filter(content => content.completed).length;
      return Math.round((completedCount / currentModule.value.contents.length) * 100);
    };

    // Mark a content as complete/incomplete for the current module.
    // This method is used by both the checkbox in the accordion list, and the "Mark as Complete" button above the video.
    const onContentComplete = async (moduleId: string, contentId: string): Promise<void> => {
      try {
        const response = await axios.post('/progress/complete', {
          courseId: course.value._id,
          moduleId,
          contentId,
        });
        console.log(response.data.message);
        // Find the content in currentModule and toggle its completed flag based on the response message.
        const content = currentModule.value.contents.find(cont => cont._id === contentId);
        if (content) {
          if (response.data.message === 'Content marked as complete') {
            content.completed = true;
          } else if (response.data.message === 'Content marked as incomplete') {
            content.completed = false;
          }
        }
      } catch (err) {
        console.error('Error marking content as complete/incomplete:', err);
      }
    };

    return {
      settings,
      router,
      course,
      currentModule,
      currentContent,
      signedVideoUrl,
      loading,
      error,
      fetchCourseDetails,
      toggleModule,
      getContentCount,
      breadcrumbItems,
      computeModuleProgress,
      onContentComplete,
    };
  },
});
</script>
